import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { API_BASE_PUBLIC_URL } from '../../utils';

/** `public/spversions/get-all` endpoint'i — interceptor buni keshlamaydi (NEVER_CACHE). */
export const SP_VERSIONS_PATH = 'public/spversions/get-all';

/**
 * Frontend URL segmenti  →  `spversions/get-all` dagi kalit.
 * Yangi `sp*` endpoint qo'shilsa shu yerga ham qo'shing.
 */
export const SP_VERSION_KEY: Record<string, string> = {
  spregions: 'regions',
  spdistricts: 'districts',
  spschoolgrades: 'school-grades',
  spschoolsubjects: 'subjects',
  spstudyyears: 'study-years',
  spinstitutions: 'institutions',
  spinstitutionclasses: 'institution-classes',
  sporganizationtypes: 'organization-types',
  spschoollegalforms: 'school-legal-forms',
  spschooltypes: 'school-types',
  spschoolmanagementforms: 'school-management-forms',
  sptranslations: 'translations',
  splanguages: 'languages',
  spanswersheettemplate: 'answer-sheet-templates',
  roles: 'roles',
  permissions: 'roles',
};

interface SpVersionRow {
  key: string;
  version: number;
  changedAt: string;
}

interface SpVersionResponse {
  result: SpVersionRow[];
}

/**
 * `spversions/get-all` ni saqlaydi va throttle bilan yangilaydi.
 * `httpCacheInterceptor` `sp*` kesh yozuvining versiyasini shu bilan solishtiradi.
 */
@Injectable({ providedIn: 'root' })
export class SpVersionService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_PUBLIC_URL);

  private readonly versions = new Map<string, number>();
  private loaded = false;
  private inFlight?: Observable<void>;

  /** URL segmenti bo'yicha joriy versiya (yuklanmagan bo'lsa — undefined). */
  versionFor(segment: string): number | undefined {
    const key = SP_VERSION_KEY[segment];
    return key ? this.versions.get(key) : undefined;
  }

  /**
   * Versiyalar bir marta — sahifa yuklanганда (yoki F5/logout/kesh tozalashdan keyin)
   * olinadi va sessiya davomida ushlab turiladi. Foydalanuvchiga yangi `sp*` ma'lumot
   * kerak bo'lsa F5 bosadi. Sahifa har ochilganда oldingi sessiyada IndexedDB'ga
   * yozilган `sp*` kesh joriy versiya bilan tekshiriladi va eskilari yangilanadi.
   */
  ensureFresh(): Observable<void> {
    if (this.loaded) {
      return of(undefined);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.http
      .get<SpVersionResponse>(`${this.apiBaseUrl}${SP_VERSIONS_PATH}`)
      .pipe(
        tap((res) => {
          this.versions.clear();
          for (const row of res?.result ?? []) {
            this.versions.set(row.key, row.version);
          }
          this.loaded = true;
        }),
        map(() => undefined),
        catchError(() => of(undefined)),
        finalize(() => (this.inFlight = undefined)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.inFlight;
  }

  /** Keyingi `ensureFresh()` majburan qayta so'raydi (logout / kesh tozalash). */
  reset(): void {
    this.loaded = false;
    this.versions.clear();
  }
}
